<template>
  <div v-if="showCampaignOffer" class="referral-banner">
    <component
      :is="isAuthenticated ? 'a' : 'button'"
      v-bind="linkAttrs"
      class="referral-banner__link"
      :data-placement="placement"
      :aria-label="copy.ariaLabel"
      @click="onClick"
    >
      <span class="referral-banner__inner">
        <img
          class="referral-banner__owl"
          src="/static/img/yagiprime-referral/owl-letter.png"
          alt=""
          width="400"
          height="299"
        />
        <span class="referral-banner__body">
          <span class="referral-banner__badge">{{ copy.badge }}</span>
          <span class="referral-banner__title">
            <span
              v-for="line in copy.titleLines"
              :key="line"
              class="referral-banner__title-line"
              >{{ line }}</span
            >
          </span>
        </span>
        <span class="referral-banner__circle" aria-hidden="true">
          <span class="referral-banner__circle-lead">{{
            copy.circleLead
          }}</span>
          <span class="referral-banner__circle-main">{{
            copy.circleMain
          }}</span>
        </span>
        <svg
          class="referral-banner__chevron"
          viewBox="0 0 12 20"
          aria-hidden="true"
        >
          <path d="M2 2l8 8-8 8" />
        </svg>
      </span>
    </component>
  </div>
</template>

<script setup lang="ts">
/**
 * ヤギプライム紹介割引キャンペーンの告知バナー。
 *
 * ヤギプライムへ遷移するボタン（志望動機・証明写真・確認画面の
 * ポップアップ）の近くに置き、「紹介すればお互い30日無料」を知らせる。
 * 押すとマイページ＞ヤギプライムのシェア枠へ行く。未ログインはマイページに
 * 入れない（requiresAuth で /login へ飛ばされ、編集中の画面から離れる）ので
 * ログインダイアログを開く。
 * 出し分けはシェア枠と同じ条件（useReferralOfferVisibility）。
 */
import { computed } from 'vue'
import { emitOpenLoginDialog } from '@/plugins/emitter'
import { useReferralOfferVisibility } from '@/composables/yagiprime/useReferralOfferVisibility'
import campaign from '@/utils/yagiprime/referralCampaign.json'

const props = withDefaults(
  defineProps<{
    /** 計測（GTM の link_click）でどこのバナーかを区別する識別子 */
    placement: string
    /** 履歴書の編集中など、今の画面を離れさせたくない置き場所では別タブで開く */
    newTab?: boolean
  }>(),
  { newTab: false }
)

const copy = campaign.banner

const { isAuthenticated, showCampaignOffer } = useReferralOfferVisibility()

const linkAttrs = computed(() => {
  if (!isAuthenticated.value) return { type: 'button' }
  return props.newTab
    ? { href: copy.path, target: '_blank', rel: 'noopener' }
    : { href: copy.path }
})

function onClick(): void {
  if (isAuthenticated.value) return
  emitOpenLoginDialog()
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/yagiprime/referral-campaign-panel';

$pink-strong: #e3165c;
$green: #3b8b40;

.referral-banner {
  container-type: inline-size;
  width: 100%;
  max-width: 560px;
  margin: 12px auto;
}

.referral-banner__link {
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  border-radius: 12px;
  background: none;
  color: #333;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.15s ease;

  // グローバルの a:hover / button:hover { opacity: .5 } を打ち消す
  &:hover {
    opacity: 1;
    color: #333;
    text-decoration: none;
    transform: translateY(-1px);
  }
}

.referral-banner__inner {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px 12px 10px 8px;

  // 枠線は志望動機の AI パネル・シェア枠と同じグラデーション（mixin が正本）
  @include referral-campaign-border(linear-gradient(#f1f7ec, #f1f7ec));

  border-radius: 12px;
}

.referral-banner__owl {
  flex: 0 0 auto;
  width: 88px;
  height: auto;

  // デザインは手紙が右手側。見出し（ReferralCampaignHeadline）と同じく反転して使う
  transform: scaleX(-1);
}

.referral-banner__body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  min-width: 0;
}

.referral-banner__badge {
  padding: 3px 12px;
  border-radius: 999px;
  background: $green;
  color: #fff;
  font-size: 11px;
  font-weight: bold;
  line-height: 1.5;
}

.referral-banner__title {
  display: flex;
  flex-direction: column;
  font-size: 20px;
  font-weight: bold;
  line-height: 1.3;
  letter-spacing: 0.02em;
}

.referral-banner__circle {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: $pink-strong;
  color: #fff;
  font-weight: bold;
  line-height: 1.3;
  text-align: center;
}

.referral-banner__circle-lead {
  color: #ffe66d;
  font-size: 13px;
}

.referral-banner__circle-main {
  font-size: 12px;
}

.referral-banner__chevron {
  flex: 0 0 auto;
  width: 10px;
  height: 16px;
  fill: none;
  stroke: #333;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 3;
}

// 置き場所の幅で詰める（志望動機の左カラムやポップアップは画面幅より狭い）
@container (width <= 440px) {
  .referral-banner__inner {
    gap: 8px;
    padding: 8px 8px 8px 6px;
  }

  .referral-banner__owl {
    width: 56px;
  }

  .referral-banner__badge {
    padding: 2px 8px;
    font-size: 10px;
  }

  .referral-banner__title {
    font-size: 15px;
  }

  .referral-banner__circle {
    width: 58px;
    height: 58px;
  }

  .referral-banner__circle-lead {
    font-size: 11px;
  }

  .referral-banner__circle-main {
    font-size: 10px;
  }
}

@container (width <= 300px) {
  .referral-banner__owl {
    display: none;
  }
}
</style>
